<template>
  <div class="shipping-quote-widget" :class="`shipping-quote-widget--${theme}`">
    <div class="sq-header" @click="expanded = !expanded" role="button">
      <div class="sq-header-text">
        <span class="sq-header-title">
          <template v-if="selectedRate && freeShippingTesting">Envío: GRATIS <small style="opacity:0.6">(test)</small></template>
          <template v-else-if="selectedRate">Envío: {{ fmtPrice(selectedRate.price) }}</template>
          <template v-else>Cotizá tu envío</template>
        </span>
        <span class="sq-header-sub">
          <template v-if="selectedRate">{{ selectedRate.productName }} — {{ selectedRate.deliveryTimeMin }}–{{ selectedRate.deliveryTimeMax }} días hábiles</template>
          <template v-else-if="externalPostalCode">Se calcula con tu código postal</template>
          <template v-else>Ingresá tu código postal para conocer el costo</template>
        </span>
      </div>
      <i class="fas sq-chevron" :class="expanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
    </div>

    <transition name="sq-slide">
      <div v-show="expanded" class="sq-body">
        <div v-if="!externalPostalCode" class="sq-input-row">
          <div class="sq-input-wrapper">
            <i class="fas fa-map-marker-alt sq-input-icon"></i>
            <input
              v-model="postalCode"
              type="text"
              class="form-control sq-input"
              placeholder="Ej: 1425"
              maxlength="8"
              @input="e => e.target.value = e.target.value.replace(/\D/g, '')"
              @keyup.enter="fetchRates"
            />
          </div>
          <button
            type="button"
            class="btn sq-btn"
            :disabled="loading || !postalCode || postalCode.length < 4"
            @click="fetchRates"
          >
            <i v-if="loading" class="fas fa-spinner fa-spin"></i>
            <template v-else><i class="fas fa-search me-1"></i>Cotizar</template>
          </button>
        </div>

        <div v-if="externalPostalCode && loading" class="sq-loading-ext">
          <i class="fas fa-spinner fa-spin me-2"></i>Calculando envío para CP {{ postalCode }}...
        </div>

        <div v-if="rates.length > 0" class="sq-rates">
          <div
            v-for="(rate, idx) in rates"
            :key="idx"
            class="sq-rate"
            :class="{ 'sq-rate--selected': selectedRate === rate }"
            @click="selectRate(rate, lastDimensionsUsed)"
          >
            <div class="sq-rate-radio">
              <span class="sq-radio" :class="{ 'sq-radio--on': selectedRate === rate }"></span>
            </div>
            <div class="sq-rate-left">
              <strong class="sq-rate-name">{{ rate.productName }}</strong>
              <small class="sq-rate-time">
                <i class="fas fa-clock me-1"></i>{{ rate.deliveryTimeMin }}–{{ rate.deliveryTimeMax }} días hábiles
              </small>
              <span class="badge sq-rate-badge" :class="rate.deliveredType === 'D' ? 'bg-delivery' : 'bg-pickup'">
                <i :class="rate.deliveredType === 'D' ? 'fas fa-home' : 'fas fa-building'" class="me-1"></i>
                {{ rate.deliveredType === 'D' ? 'A domicilio' : 'En sucursal' }}
              </span>
            </div>
            <span class="sq-rate-price">
              <template v-if="freeShippingTesting">
                <span style="text-decoration: line-through; opacity: 0.5; font-size: 0.8em; margin-right: 4px;">{{ fmtPrice(rate.price) }}</span>
                <span style="color: var(--success-color);">GRATIS</span>
              </template>
              <template v-else>{{ fmtPrice(rate.price) }}</template>
            </span>
          </div>
        </div>

        <div v-if="errorMsg" class="sq-error">
          <i class="fas fa-exclamation-triangle me-2"></i>{{ errorMsg }}
        </div>

        <div v-if="hasPickupRate" class="sq-branches-link">
          <button type="button" class="branches-btn" @click="showBranchModal = true">
            <i class="fas fa-map-marked-alt me-2"></i>
            {{ selectedBranch ? 'Cambiar sucursal' : 'Ver sucursales disponibles' }}
          </button>
        </div>

        <div v-if="selectedBranch && selectedRate?.deliveredType === 'S'" class="sq-branch-selected">
          <div class="sq-branch-info">
            <i class="fas fa-building me-1"></i>
            <strong>{{ selectedBranch.name }}</strong>
            <small v-if="selectedBranch.address" class="ms-1">— {{ selectedBranch.address }}, {{ selectedBranch.city }}</small>
          </div>
          <button class="sq-branch-remove" @click="clearBranch" title="Quitar sucursal">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div v-if="preferredBranch && !selectedBranch && hasPickupRate" class="sq-branch-hint" @click="usePreferredBranch">
          <i class="fas fa-star me-1"></i>
          <span>Usar sucursal guardada: <strong>{{ preferredBranch.preferred_agency_name }}</strong></span>
          <i class="fas fa-chevron-right ms-auto"></i>
        </div>

        <BranchLocatorModal
          v-model="showBranchModal"
          :initial-postal-code="postalCode"
          @branch-selected="onBranchSelected"
        />

        <div v-if="!attempted && !errorMsg && rates.length === 0 && !loading" class="sq-hint">
          <i class="fas fa-map-pin me-1"></i>
          <template v-if="externalPostalCode">Completá el código postal para calcular el envío.</template>
          <template v-else>Envíos a todo el país con Correo Argentino.</template>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { getShippingRates, getPreferredBranch } from '@/services/shipping';
import { useStore } from 'vuex';
import BranchLocatorModal from '@/components/shipping/BranchLocatorModal.vue';

// eslint-disable-next-line no-undef
const props = defineProps({
  declaredValue: { type: Number, default: 0 },
  weight: { type: Number, default: null },
  autoSelect: { type: Boolean, default: true },
  startExpanded: { type: Boolean, default: false },
  externalPostalCode: { type: String, default: '' },
  theme: {
    type: String,
    default: 'dark',
    validator: (value) => ['dark', 'light'].includes(value),
  },
});
// eslint-disable-next-line no-undef
const emit = defineEmits(['rate-selected']);

const store = useStore();
const isAuthenticated = computed(() => !!store.getters?.isAuthenticated || !!localStorage.getItem('accessToken'));

const expanded = ref(props.startExpanded);
const postalCode = ref(props.externalPostalCode || '');
const loading = ref(false);
const errorMsg = ref('');
const attempted = ref(false);
const rates = ref([]);
const lastDimensionsUsed = ref(null);
const selectedRate = ref(null);
const showBranchModal = ref(false);
const freeShippingTesting = ref(false);
const selectedBranch = ref(null);
const preferredBranch = ref(null);
let fetchDebounceTimer = null;

onMounted(async () => {
  if (isAuthenticated.value) {
    try {
      const pref = await getPreferredBranch();
      if (pref.has_preferred) {
        preferredBranch.value = pref;
      }
    } catch (e) {
      console.error('Error loading preferred branch:', e);
    }
  }
});

watch(() => props.externalPostalCode, (newCP) => {
  if (!newCP) {
    postalCode.value = '';
    rates.value = [];
    selectedRate.value = null;
    errorMsg.value = '';
    attempted.value = false;
    emit('rate-selected', null);
    return;
  }
  postalCode.value = newCP;
  if (newCP.length >= 4 && /^\d{4}/.test(newCP.replace(/\D/g, ''))) {
    clearTimeout(fetchDebounceTimer);
    fetchDebounceTimer = setTimeout(() => {
      expanded.value = true;
      fetchRates();
    }, 600);
  }
}, { immediate: true });

watch(() => props.declaredValue, () => {
  if (attempted.value && rates.value.length > 0) {
    fetchRates();
  }
});

const fetchRates = async () => {
  const pc = postalCode.value.replace(/\D/g, '').slice(0, 4);
  if (!pc || pc.length < 4) return;

  loading.value = true;
  errorMsg.value = '';
  rates.value = [];
  lastDimensionsUsed.value = null;
  const preserveDeliveredType = selectedRate.value?.deliveredType || null;
  selectedRate.value = null;

  try {
    const opts = {};
    if (props.declaredValue > 0) opts.declared_value = props.declaredValue;
    if (props.weight) opts.weight = props.weight;

    const result = await getShippingRates(pc, opts);

    freeShippingTesting.value = !!result.free_shipping_testing;

    if (result.rates && result.rates.length > 0) {
      rates.value = result.rates;
      const dims = result.dimensions_used || null;
      lastDimensionsUsed.value = dims;
      if (props.autoSelect) {
        const match = preserveDeliveredType
          ? result.rates.find((r) => r.deliveredType === preserveDeliveredType)
          : null;
        selectRate(match || result.rates[0], dims);
      }
    } else {
      errorMsg.value = 'No hay opciones de envío para ese código postal.';
    }
  } catch (err) {
    console.error('ShippingQuote error:', err);
    errorMsg.value =
      err.response?.data?.detail ||
      err.response?.data?.error ||
      'No pudimos calcular el envío. Intentá nuevamente.';
  } finally {
    loading.value = false;
    attempted.value = true;
  }
};

const selectRate = (rate, packageDimensions = null) => {
  selectedRate.value = rate;
  const rateWithFlag = rate ? {
    ...rate,
    freeShippingTesting: freeShippingTesting.value,
    ...(packageDimensions ? { packageDimensions } : {}),
    ...(selectedBranch.value && rate.deliveredType === 'S' ? {
      branchCode: selectedBranch.value.code,
      branchName: selectedBranch.value.fullName || selectedBranch.value.name,
    } : {}),
  } : null;
  emit('rate-selected', rateWithFlag);
};

const onBranchSelected = (branch) => {
  selectedBranch.value = branch;
  const pickupRate = rates.value.find(r => r.deliveredType === 'S');
  if (pickupRate) {
    selectRate(pickupRate, lastDimensionsUsed.value);
  }
};

const clearBranch = () => {
  selectedBranch.value = null;
  if (selectedRate.value) {
    const rateWithFlag = {
      ...selectedRate.value,
      freeShippingTesting: freeShippingTesting.value,
      ...(lastDimensionsUsed.value ? { packageDimensions: lastDimensionsUsed.value } : {}),
    };
    emit('rate-selected', rateWithFlag);
  }
};

const usePreferredBranch = () => {
  if (!preferredBranch.value) return;
  selectedBranch.value = {
    code: preferredBranch.value.preferred_agency_code,
    name: preferredBranch.value.preferred_agency_name,
    fullName: preferredBranch.value.preferred_agency_name,
    address: '',
    city: '',
    province: '',
    postalCode: '',
    savedForNext: true,
  };
  const pickupRate = rates.value.find(r => r.deliveredType === 'S');
  if (pickupRate) {
    selectRate(pickupRate, lastDimensionsUsed.value);
  }
};

const fmtPrice = (value) => {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value);
};

const hasPickupRate = computed(() => rates.value.some(r => r.deliveredType === 'S'));
</script>

<style scoped>
.shipping-quote-widget {
  --sq-surface: rgba(255, 255, 255, 0.03);
  --sq-border: rgba(255, 255, 255, 0.08);
  --sq-header-bg: linear-gradient(90deg, rgba(129, 129, 129, 0.12) 0%, transparent 100%);
  --sq-header-bg-hover: linear-gradient(90deg, rgba(116, 116, 116, 0.18) 0%, transparent 100%);
  --sq-body-border: rgba(255, 255, 255, 0.06);
  --sq-text: #fff;
  --sq-text-strong: #fff;
  --sq-muted: rgba(255, 255, 255, 0.62);
  --sq-input-bg: rgba(255, 255, 255, 0.06);
  --sq-input-border: rgba(255, 255, 255, 0.12);
  --sq-rate-bg: rgba(255, 255, 255, 0.04);
  --sq-rate-bg-hover: rgba(255, 255, 255, 0.07);
  --sq-rate-selected-bg: rgba(var(--primary-color-rgb), 0.12);
  --sq-control-surface: rgba(var(--primary-color-rgb), 0.08);
  --sq-control-border: rgba(var(--primary-color-rgb), 0.2);
  background: var(--sq-surface);
  border: 1px solid var(--sq-border);
  border-radius: var(--radius-md, 10px);
  color: var(--sq-text);
  overflow: hidden;
  margin-top: 1rem;
}

.shipping-quote-widget--light {
  --sq-surface: #fff;
  --sq-border: var(--border-light, #e5e7eb);
  --sq-header-bg: linear-gradient(90deg, #f8fafc 0%, #fff 100%);
  --sq-header-bg-hover: linear-gradient(90deg, #f1f5f9 0%, #fff 100%);
  --sq-body-border: var(--border-light, #e5e7eb);
  --sq-text: var(--text-primary, #111827);
  --sq-text-strong: var(--text-primary, #111827);
  --sq-muted: var(--text-muted, #64748b);
  --sq-input-bg: #fff;
  --sq-input-border: var(--border-color, #d1d5db);
  --sq-rate-bg: #f8fafc;
  --sq-rate-bg-hover: #f1f5f9;
  --sq-rate-selected-bg: rgba(var(--primary-color-rgb), 0.08);
  --sq-control-surface: rgba(var(--primary-color-rgb), 0.08);
  --sq-control-border: rgba(var(--primary-color-rgb), 0.2);
}

.sq-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  background: var(--sq-header-bg);
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;
}
.sq-header:hover { background: var(--sq-header-bg-hover); }

.sq-header-text { display: flex; flex-direction: column; flex: 1; }
.sq-header-title {
  color: var(--sq-text-strong);
  font-size: 0.92rem;
  font-weight: 700;
  line-height: 1.2;
}
.sq-header-sub {
  color: var(--sq-muted);
  font-size: 0.72rem;
  margin-top: 1px;
}
.sq-chevron {
  color: var(--sq-muted);
  font-size: 0.7rem;
}

.sq-slide-enter-active,
.sq-slide-leave-active {
  transition: max-height 0.35s ease, opacity 0.25s ease;
  overflow: hidden;
}
.sq-slide-enter-from,
.sq-slide-leave-to {
  max-height: 0;
  opacity: 0;
}
.sq-slide-enter-to,
.sq-slide-leave-from {
  max-height: 600px;
  opacity: 1;
}

.sq-body {
  padding: 0.85rem 1rem 1rem;
  border-top: 1px solid var(--sq-body-border);
}

.sq-input-row {
  display: flex;
  gap: 0.5rem;
}

.sq-input-wrapper {
  flex: 1;
  position: relative;
}

.sq-input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--sq-muted);
  font-size: 0.85rem;
}

.sq-input {
  border-radius: var(--radius-sm, 6px);
  font-size: 0.9rem;
  padding-left: 34px;
  background: var(--sq-input-bg) !important;
  border: 1px solid var(--sq-input-border) !important;
  color: var(--sq-text) !important;
}
.sq-input::placeholder { color: var(--sq-muted); }
.sq-input:focus {
  border-color: var(--primary-color) !important;
  box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.15) !important;
}

.sq-btn {
  white-space: nowrap;
  border-radius: var(--radius-sm, 6px);
  font-weight: 600;
  font-size: 0.85rem;
  min-width: 100px;
  background: var(--primary-color);
  color: #fff !important;
  border: none;
  transition: background 0.2s, transform 0.1s;
}
.sq-btn:hover:not(:disabled) { background: var(--primary-dark); transform: translateY(-1px); color: #fff !important; }
.sq-btn:disabled { opacity: 0.45; cursor: not-allowed; }

.sq-rates {
  margin-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.sq-rate {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 0.9rem;
  border-radius: var(--radius-md, 10px);
  background: var(--sq-rate-bg);
  border: 2px solid transparent;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}
.sq-rate:hover { background: var(--sq-rate-bg-hover); }
.sq-rate--selected {
  border-color: var(--primary-color);
  background: var(--sq-rate-selected-bg);
}

.sq-rate-radio { flex-shrink: 0; }
.sq-radio {
  display: block;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid var(--border-color, #e5e5e5);
  transition: border-color 0.2s;
  position: relative;
}
.sq-radio--on {
  border-color: var(--primary-color);
}
.sq-radio--on::after {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--primary-color);
}

.bg-delivery {
  background-color: rgba(var(--primary-color-rgb), 0.15);
  color: var(--primary-dark);
}
.bg-pickup {
  background-color: rgba(var(--primary-color-rgb), 0.2);
  color: var(--primary-dark);
}
.sq-rate-left { display: flex; flex-direction: column; gap: 2px; flex: 1; }
.sq-rate-name { color: var(--sq-text-strong); font-size: 0.86rem; }
.sq-rate-time { color: var(--sq-muted); font-size: 0.76rem; }
.sq-rate-badge { width: fit-content; font-size: 0.68rem; margin-top: 3px; }
.sq-rate-price {
  color: var(--sq-text-strong);
  font-weight: 700;
  font-size: 1rem;
  white-space: nowrap;
}

@media (max-width: 575.98px) {
  .sq-rate {
    flex-wrap: wrap;
    align-items: flex-start;
  }

  .sq-rate-radio {
    margin-top: 2px;
  }

  .sq-rate-left {
    min-width: 0;
    flex: 1 1 100%;
  }

  .sq-rate-price {
    width: 100%;
    text-align: right;
    font-size: 0.95rem;
  }
}

.sq-error {
  margin-top: 0.75rem;
  background: rgba(239, 68, 68, 0.12);
  color: var(--danger-color, #ef4444);
  border-radius: var(--radius-sm, 6px);
  padding: 0.6rem 0.75rem;
  font-size: 0.82rem;
}
.sq-hint {
  margin-top: 0.6rem;
  color: var(--sq-muted);
  font-size: 0.78rem;
}

.sq-branches-link {
  margin-top: 0.65rem;
  text-align: center;
}

.branches-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--primary-color);
  font-size: 0.78rem;
  font-weight: 500;
  text-decoration: none;
  padding: 0.4rem 0.75rem;
  border-radius: var(--radius-sm, 6px);
  background: var(--sq-control-surface);
  border: 1px solid var(--sq-control-border);
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}

.branches-btn:hover {
  background: rgba(var(--primary-color-rgb), 0.14);
  border-color: rgba(var(--primary-color-rgb), 0.35);
  color: var(--primary-color);
}

.sq-loading-ext {
  color: var(--sq-muted);
  font-size: 0.84rem;
  padding: 0.4rem 0;
}

.sq-branch-selected {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.6rem;
  padding: 0.55rem 0.75rem;
  border-radius: var(--radius-sm, 6px);
  background: var(--sq-control-surface);
  border: 1px solid var(--sq-control-border);
}

.sq-branch-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.15rem;
  font-size: 0.76rem;
  color: var(--sq-text-strong);
}
.sq-branch-info i { color: var(--primary-color); }
.sq-branch-info small { color: var(--sq-muted); font-size: 0.68rem; }

.sq-branch-remove {
  background: none;
  border: none;
  color: var(--sq-muted);
  cursor: pointer;
  padding: 0.2rem;
  font-size: 0.7rem;
}
.sq-branch-remove:hover { color: var(--danger-color); }

.sq-branch-hint {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.55rem;
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius-sm, 6px);
  background: rgba(245, 158, 11, 0.06);
  border: 1px solid rgba(245, 158, 11, 0.15);
  color: var(--sq-muted);
  font-size: 0.72rem;
  cursor: pointer;
}
.sq-branch-hint:hover {
  background: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.25);
}
.sq-branch-hint i:first-child { color: var(--warning-color); }
.sq-branch-hint strong { color: var(--sq-text-strong); }
</style>
